import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { authApi, signIn } from '@/api/auth';
import { friendlyMessage } from '@/lib/errors';
import { emitQrEvent } from '@/api/qrAnalytics';
import { getOrCreateQrSessionId } from '@/lib/qrSession';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { BowArc } from '@/components/ui/BowArc';
import { Eyebrow, Display } from '@/components/ui/Typography';
import { registerSchema, type RegisterFormData } from '@/lib/validation';
import { formatCPF, formatPhone, unmaskDigits } from '@/lib/masks';
import { useKeyboardViewport } from '@/hooks/useKeyboardViewport';
import {
  getPendingReferralCode,
  normalizeReferralCode,
  setPendingReferralCode
} from '@/lib/referral';

interface LocationState {
  from?: { pathname: string };
}

export default function Register() {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting }
  } = useForm<RegisterFormData>({ resolver: zodResolver(registerSchema) });
  const navigate = useNavigate();
  const location = useLocation();
  // Preserva destino quando o usuário cai aqui via deeplink (ex: scan QR
  // físico → landing → "Carregar agora" → /login → "Crie sua conta"). Bate
  // com o padrão do Login.tsx:27,43,66.
  const from = (location.state as LocationState | null)?.from?.pathname ?? '/';
  const [apiError, setApiError] = useState<string | null>(null);
  const errorRef = useRef<HTMLParagraphElement>(null);
  const phoneRef = useRef<HTMLInputElement | null>(null);
  const { kbOpen, vvHeight } = useKeyboardViewport();
  // Indique e Ganhe — código de quem indicou (opcional). Pré-preenche com o
  // pendente da landing /convite; só GUARDA o pendente — quem aplica é o
  // ReferralClaimer depois do login (cobre também Google/Apple).
  const [refCode, setRefCode] = useState(() => getPendingReferralCode() ?? '');
  const [refOpen, setRefOpen] = useState(() => getPendingReferralCode() !== null);

  // Com o teclado aberto o miolo corta logo abaixo do campo em edição e o
  // usuário não descobre os campos seguintes (bug 2026-07-04, print do CPF).
  // Centralizar o campo focado no container rolável faz o próximo campo
  // aparecer espiando abaixo. Delay dá tempo do teclado/frame assentarem.
  function revealFocusedField(e: React.FocusEvent<HTMLElement>) {
    const el = e.target;
    if (!(el instanceof HTMLElement) || el.tagName !== 'INPUT') return;
    window.setTimeout(() => {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 250);
  }

  useEffect(() => {
    if (apiError) {
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [apiError]);

  async function onSubmit(data: RegisterFormData) {
    setApiError(null);
    const normalizedRef = normalizeReferralCode(refCode);
    if (normalizedRef) setPendingReferralCode(normalizedRef);
    try {
      await authApi.register({
        email: data.email,
        phone: unmaskDigits(data.phone),
        cpf: unmaskDigits(data.cpf),
        name: data.name,
        password: data.password,
        consent_terms: true,
        consent_privacy: true
      });
      const user = await signIn(data.email, data.password);
      const m = /^\/qr\/([^/?#]+)/.exec(from);
      if (m) {
        emitQrEvent({
          qrSessionId: getOrCreateQrSessionId(),
          hubStationId: decodeURIComponent(m[1]),
          eventType: 'auth_completed'
        });
      }
      if (!user.profile_complete) {
        navigate('/complete-profile', { replace: true, state: { from: { pathname: from } } });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      setApiError(friendlyMessage(err, 'Não foi possível conectar ao servidor'));
    }
  }

  // NSS (design/no-scroll/nss-auth.jsx · RegisterNS): o formulário mais alto
  // do app. Viewport fixa com o corpo rolável e o CTA sempre ancorado — com
  // fonte de acessibilidade grande o corpo ROLA em vez de clipar (§5.2).
  // Teclado aberto: header colapsa e some o rodapé (§5.3).
  return (
    <div
      className="mx-auto flex max-w-sm flex-col overflow-hidden px-7"
      style={{
        height: kbOpen && vvHeight !== null ? `${vvHeight}px` : '100dvh',
        paddingTop: kbOpen ? 10 : 'max(1.75rem, env(safe-area-inset-top))',
        paddingBottom: kbOpen ? 10 : 'max(1.5rem, env(safe-area-inset-bottom))'
      }}
    >
      <div className="flex-shrink-0">
        <div
          className={`flex items-center gap-2.5 ${
            kbOpen ? 'mb-2' : 'mb-6 [@media(max-height:740px)]:mb-3'
          }`}
        >
          <BowArc size={kbOpen ? 24 : 32} />
          <span className="text-lg font-semibold tracking-tighter">Bow</span>
        </div>

        {kbOpen ? (
          <Display size={20} className="mb-2">
            Criar sua conta.
          </Display>
        ) : (
          <>
            <Eyebrow>Vamos começar</Eyebrow>
            <Display
              size={32}
              className="mt-2.5 [@media(max-height:740px)]:mt-1.5 [@media(max-height:740px)]:!text-[24px]"
            >
              Criar sua conta.
            </Display>
            <p className="mt-2 text-sm leading-relaxed text-bow-royal/60 [@media(max-height:740px)]:hidden">
              Precisamos de alguns dados básicos para liberar sua primeira recarga.
            </p>

            {/* Stepper visual */}
            <div className="mt-5 flex gap-1.5 [@media(max-height:740px)]:mt-3">
              <span className="h-1 flex-1 rounded-full bg-bow-royal" />
              <span className="h-1 flex-1 rounded-full bg-bow-royal/10" />
              <span className="h-1 flex-1 rounded-full bg-bow-royal/10" />
            </div>
          </>
        )}
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex min-h-0 flex-1 flex-col"
      >
        {/* Corpo — rola quando não cabe (fonte grande / teclado aberto) */}
        <div
          onFocusCapture={revealFocusedField}
          className="mt-5 flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto [@media(max-height:740px)]:mt-3 [@media(max-height:740px)]:gap-2"
        >
        <Input
          label="Nome completo"
          autoComplete="name"
          error={errors.name?.message}
          {...register('name')}
        />
        <Input
          label="E-mail"
          type="email"
          autoComplete="email"
          error={errors.email?.message}
          {...register('email')}
        />
        <Controller
          control={control}
          name="cpf"
          render={({ field }) => (
            <Input
              label="CPF"
              inputMode="numeric"
              autoComplete="off"
              placeholder="000.000.000-00"
              error={errors.cpf?.message}
              value={formatCPF(field.value ?? '')}
              onChange={(e) => {
                const v = formatCPF(e.target.value);
                field.onChange(v);
                // CPF tem tamanho fixo: completo → pula pro Celular (que
                // estava escondido sob a dobra do teclado).
                if (unmaskDigits(v).length === 11) phoneRef.current?.focus();
              }}
              onBlur={field.onBlur}
              ref={field.ref}
              name="bow-cpf"
            />
          )}
        />
        <Controller
          control={control}
          name="phone"
          render={({ field }) => (
            <Input
              label="Celular"
              type="tel"
              autoComplete="off"
              inputMode="numeric"
              placeholder="(27) 99999-0000"
              error={errors.phone?.message}
              value={formatPhone(field.value ?? '')}
              onChange={(e) => field.onChange(formatPhone(e.target.value))}
              onBlur={field.onBlur}
              ref={(el) => {
                field.ref(el);
                phoneRef.current = el;
              }}
              name="bow-phone"
            />
          )}
        />
        <Input
          label="Senha"
          type="password"
          autoComplete="new-password"
          error={errors.password?.message}
          {...register('password')}
        />

        {/* Indique e Ganhe — campo opcional recolhido (abre sozinho se já
            veio código da landing /convite). */}
        {refOpen ? (
          <Input
            label="Código de indicação (opcional)"
            autoComplete="off"
            autoCapitalize="characters"
            placeholder="EX.: MARIA-7K2"
            value={refCode}
            onChange={(e) => setRefCode(e.target.value.toUpperCase())}
            name="bow-ref"
          />
        ) : (
          <button
            type="button"
            onClick={() => setRefOpen(true)}
            className="self-start px-1 text-[13px] font-semibold text-bow-royal underline-offset-2 hover:underline"
          >
            Tenho um código de indicação
          </button>
        )}
        <ConsentRow>
          <input type="checkbox" className="sr-only peer" {...register('consent_terms')} />
          <CheckBox />
          <span>
            Aceito os{' '}
            <a
              href="https://bow.app.br/privacidade#termos"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold underline-offset-2 hover:underline"
            >
              Termos de uso
            </a>
          </span>
        </ConsentRow>
        {errors.consent_terms && (
          <span className="px-1 text-sm text-bow-red">{errors.consent_terms.message}</span>
        )}
        <ConsentRow>
          <input type="checkbox" className="sr-only peer" {...register('consent_privacy')} />
          <CheckBox />
          <span>
            Concordo com a{' '}
            <a
              href="https://bow.app.br/privacidade"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold underline-offset-2 hover:underline"
            >
              Política de Privacidade
            </a>{' '}
            (LGPD)
          </span>
        </ConsentRow>
        {errors.consent_privacy && (
          <span className="px-1 text-sm text-bow-red">{errors.consent_privacy.message}</span>
        )}
        {apiError && <p ref={errorRef} className="px-1 text-sm text-bow-red">{apiError}</p>}
        </div>

        {/* CTA ancorado — nunca sai da tela nem fica sob o teclado */}
        <div className="flex-shrink-0 pt-3">
          <Button
            type="submit"
            disabled={isSubmitting}
            onPointerDown={(e) => e.preventDefault()}
          >
            {isSubmitting ? 'Criando…' : 'Criar conta'}
          </Button>
        </div>
      </form>

      <p
        className={`mt-3 flex-shrink-0 text-center text-[13px] text-bow-royal/55 ${
          kbOpen ? 'hidden' : ''
        }`}
      >
        Já tem conta?{' '}
        <Link
          to="/login"
          state={location.state}
          className="font-semibold text-bow-royal"
        >
          Entrar
        </Link>
      </p>
    </div>
  );
}

function ConsentRow({ children }: { children: React.ReactNode }) {
  return (
    <label className="flex cursor-pointer items-start gap-3 py-1 text-[13px] leading-snug text-bow-royal/70">
      {children}
    </label>
  );
}

function CheckBox() {
  // Sibling check via peer: input com `peer` antes deste span aplica os
  // estilos checked (`peer-checked:*`).
  return (
    <span
      aria-hidden
      className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border-[1.5px] border-bow-royal/40 bg-white text-bow-cream transition peer-checked:border-bow-royal peer-checked:bg-bow-royal"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-3 w-3"
      >
        <path d="m5 12 5 5L20 7" />
      </svg>
    </span>
  );
}
